import React, { useEffect, useState } from 'react';
import { getRequest, getBaseUrl } from '../UrlRequest';
import { useLocation } from 'react-router-dom';
import Loading from '../common/Loading';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ko from 'date-fns/locale/ko';

const StatusItem = ({ label, value }) => (
  <div 
    style={{
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      padding: '12px',
      margin: '8px 0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      border: '1px solid #e9ecef'
    }}
    role="group"
    aria-label={`${label} 정보`}
  >
    <strong 
      style={{
        color: '#495057',
        display: 'block',
        marginBottom: '5px',
        fontSize: '0.9rem',
        fontWeight: '600'
      }}
    >
      {label}:
    </strong>
    <span 
      style={{
        color: '#2c3e50',
        fontSize: '1rem',
        lineHeight: '1.5'
      }}
    >
      {value}
    </span>
  </div>
);

const Condition = ({ patientId, selectedDate: propSelectedDate }) => {
  const [conditionData, setConditionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [surgeryImage, setSurgeryImage] = useState(null);
  // 항상 Date 객체를 유지하도록 보정
  const [selectedDate, setSelectedDate] = useState(() => (
    propSelectedDate ? (propSelectedDate instanceof Date ? propSelectedDate : new Date(propSelectedDate)) : new Date()
  ));
  const [monthlyConditionDates, setMonthlyConditionDates] = useState([]);
  const [recentConditionData, setRecentConditionData] = useState(null);
  const location = useLocation();
  const application = location.state?.application || 'hearton';

  const fetchSurgeryImage = async (healthConditionId, token) => {
    try {
      const response = await fetch(`${getBaseUrl(application)}/doctor/${healthConditionId}/surgery-image`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      if (!response.ok) throw new Error('이미지를 불러오지 못했습니다.');
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error(`Health Condition ID ${healthConditionId} 이미지 오류:`, error);
      return null;
    }
  };

  const fetchRecentConditionData = async () => {
    if (!patientId) return;
    
    setConditionData(null);
    setError(null);
    setLoading(true);
    setSurgeryImage(null);
    
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('인증 토큰이 없습니다.');
        setLoading(false);
        return;
      }

      const data = await getRequest(
        application,
        `/doctor/${patientId}/health-condition/recent`,
        token
      );

      if (data && data.on_date) {
        setRecentConditionData(data);
        const recentDate = new Date(data.on_date);
        setSelectedDate(recentDate);
        setConditionData(data);
        
        fetchMonthlyConditionDates(recentDate);
        
        if (data.health_condition_id) {
          const imageUrl = await fetchSurgeryImage(data.health_condition_id, token);
          setSurgeryImage(imageUrl);
        }
      } else {
        setError('최근 건강 상태 데이터가 없습니다.');
        const today = new Date();
        setSelectedDate(today);
        fetchMonthlyConditionDates(today);
      }
    } catch (error) {
      console.error('최근 건강상태 데이터 조회 오류:', error);
      setError('건강 상태 데이터를 불러오지 못했습니다.');
      const today = new Date();
      setSelectedDate(today);
      fetchMonthlyConditionDates(today);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyConditionDates = async (date) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('인증 토큰이 없습니다.');
        return;
      }
      
      const safeDate = date instanceof Date ? date : new Date(date);
      const year = safeDate.getFullYear();
      const month = safeDate.getMonth() + 1;
      
      const data = await getRequest(
        application,
        `/doctor/${patientId}/health-condition/record?year=${year}&month=${month}`,
        token
      );
      
      if (data && data.recorded_list) {
        const conditionDates = Object.entries(data.recorded_list)
          .filter(([_, hasData]) => hasData)
          .map(([dateStr]) => {
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
          });
        setMonthlyConditionDates(conditionDates);
      }
    } catch (error) {
      console.error('월별 건강상태 데이터 조회 오류:', error);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchConditionData(date);
  };

  const handleMonthChange = (date) => {
    fetchMonthlyConditionDates(date);
  };

  const fetchConditionData = async (date) => {
    if (!patientId || !date) return;
    
    setConditionData(null);
    setError(null);
    setLoading(true);
    setSurgeryImage(null);

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('인증 토큰이 없습니다.');
        setLoading(false);
        return;
      }

      const data = await getRequest(
        application,
        `/doctor/${patientId}/health-condition/daily?on-date=${(date instanceof Date ? date : new Date(date)).toISOString().split('T')[0]}`,
        token
      );
      if (data) {
        setConditionData(data);

        if (data.health_condition_id) {
          const imageUrl = await fetchSurgeryImage(data.health_condition_id, token);
          setSurgeryImage(imageUrl);
        }
      } else {
        setError('해당 날짜에 건강 상태 데이터가 없습니다.');
      }
    } catch (error) {
      setError('건강 상태 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) {
      fetchRecentConditionData();
    }
  }, [patientId]);

  // 상위에서 전달된 날짜(propSelectedDate)가 변경되면 동기화 및 데이터 로드
  useEffect(() => {
    if (propSelectedDate) {
      const d = propSelectedDate instanceof Date ? propSelectedDate : new Date(propSelectedDate);
      setSelectedDate(d);
      fetchMonthlyConditionDates(d);
      fetchConditionData(d);
    }
  }, [propSelectedDate]);

  const renderConditionData = (data) => (
    <div style={{
      height: '100%',
      overflow: 'auto'
    }}>
      <h3 style={{
        color: '#2c3e50',
        borderBottom: '2px solid #3498db',
        paddingBottom: '10px',
        marginBottom: '20px',
        top: 0,
        backgroundColor: 'white',
      }}>건강 상태({selectedDate.toISOString().split('T')[0]})</h3>
      
      {surgeryImage && (
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <h4 style={{
            color: '#2c3e50',
            marginBottom: '10px'
          }}>수술 이미지</h4>
          <img 
            src={surgeryImage} 
            alt="수술 이미지" 
            style={{
              width: '100%',
              height: '300px',
              objectFit: 'contain',
              borderRadius: '6px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }} 
          />
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '15px'
      }}>
        <StatusItem label="실신" value={data.fainting} />
        <StatusItem label="소변 감소" value={data.urine_reduction} />
        <StatusItem label="불편감" value={data.discomfort} />
        <StatusItem label="식사 상태" value={data.diet_status} />
        <StatusItem label="운동 빈도" value={data.exercise_frequency || '없음'} />
        <StatusItem label="심박수(BPM)" value={`${data.heart_rate || '없음'}`} />
        <StatusItem label="수술 부위 상태" value={data.surgery_site_condition || '없음'} />
        <StatusItem label="부종" value={data.edema || '없음'} />
        <StatusItem label="심부전 클래스" value={data.heart_failure_class || '없음'} />
        <StatusItem label="약물 복용 상태" value={data.medication_status} />
        <StatusItem label="기기 알람" value={data.device_alarm || '없음'} />
      </div>

      {data.discomfort === '있음' && (
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '15px'
        }}>
          <h4 style={{
            color: '#2c3e50',
            marginBottom: '10px'
          }}>불편감 상세 증상</h4>
          <div style={{
            maxHeight: '120px',
            overflowY: 'auto',
            padding: '10px',
            backgroundColor: 'white',
            borderRadius: '6px',
            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            {data.discomfort_symptom || '없음'}
          </div>
        </div>
      )}

      {data.surgery_site_symptom && (
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          padding: '15px',
          marginTop: '15px'
        }}>
          <h4 style={{
            color: '#2c3e50',
            marginBottom: '10px'
          }}>수술 부위 상세 증상</h4>
          <div style={{
            maxHeight: '120px',
            overflowY: 'auto',
            padding: '10px',
            backgroundColor: 'white',
            borderRadius: '6px',
            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            {data.surgery_site_symptom || '없음'}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <div style={{ 
        flex: '0 0 auto',
        position: 'sticky',
        top: 0,
        width: '100%',
        backgroundColor: 'white',
        zIndex: 1000
      }}>
        <div style={{
          position: 'relative',
          width: '100%'
        }}>
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            onMonthChange={handleMonthChange}
            locale={ko}
            dateFormat="yyyy년 MM월 dd일"
            showMonthYearPicker={false}
            formatWeekDay={nameOfDay => nameOfDay.substring(0, 1)}
            highlightDates={monthlyConditionDates}
            inline
            className="custom-datepicker"
            calendarClassName="custom-calendar"
            dayClassName={date => {
              return monthlyConditionDates.some(conditionDate => 
                date.getFullYear() === conditionDate.getFullYear() &&
                date.getMonth() === conditionDate.getMonth() &&
                date.getDate() === conditionDate.getDate()
              ) ? 'highlighted-date' : undefined;
            }}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled
            }) => (
              <div
                style={{
                  margin: 10,
                  display: "flex",
                  justifyContent: "center",
                  fontSize: "1.5rem"
                }}
              >
                <button 
                  onClick={decreaseMonth} 
                  disabled={prevMonthButtonDisabled}
                  style={{ fontSize: "1.5rem", padding: "0 10px" }}
                >
                  {"<"}
                </button>
                <div style={{ margin: "0 10px" }}>
                  {new Date(date).getFullYear()}년 {new Date(date).getMonth() + 1}월
                </div>
                <button 
                  onClick={increaseMonth} 
                  disabled={nextMonthButtonDisabled}
                  style={{ fontSize: "1.5rem", padding: "0 10px" }}
                >
                  {">"}
                </button>
              </div>
            )}
            customInput={
              <input
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  width: '100%',
                  fontSize: '1.5rem'
                }}
              />
            }
            wrapperClassName="datepicker-wrapper"
          />
          <style>
            {`
              .datepicker-wrapper {
                width: 100%;
              }
              .custom-calendar {
                width: 100% !important;
                font-size: 1.5rem !important;
              }
              .custom-calendar .react-datepicker__month-container {
                width: 100%;
              }
              .custom-calendar .react-datepicker__month {
                width: 100%;
              }
              .custom-calendar .react-datepicker__week {
                display: flex;
                justify-content: space-around;
              }
              .custom-calendar .react-datepicker__day-names {
                display: flex;
                justify-content: space-around;
              }
              .custom-calendar .react-datepicker__day {
                font-size: 1.5rem !important;
                line-height: 2rem !important;
                width: 2rem !important;
                margin: 0.5rem !important;
              }
              .custom-calendar .react-datepicker__day-name {
                font-size: 1.5rem !important;
                width: 2rem !important;
                line-height: 2rem !important;
                margin: 0.5rem !important;
              }
              .highlighted-date {
                background-color: #4CAF50 !important;
                border-radius: 50%;
                color: white !important;
                font-weight: bold;
              }
              .highlighted-date:hover {
                background-color: #45a049 !important;
              }
              .react-datepicker {
                font-family: 'Arial', sans-serif;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              .react-datepicker__header {
                background-color: #f8f9fa;
                border-bottom: 1px solid #e0e0e0;
                border-top-left-radius: 8px;
                border-top-right-radius: 8px;
                padding-top: 10px;
              }
              .react-datepicker__day {
                margin: 0.2rem;
                width: 2rem;
                height: 2rem;
                line-height: 2rem;
                border-radius: 50%;
                transition: all 0.2s ease-in-out;
              }
              .react-datepicker__day:hover {
                background-color: #e9ecef;
              }
              .react-datepicker__day--selected {
                background-color: #007bff !important;
                color: white !important;
              }
              .react-datepicker__day--keyboard-selected {
                background-color: #007bff !important;
                color: white !important;
              }
            `}
          </style>
        </div>
      </div>

      <div style={{ 
        flex: '1 1 auto',
        overflowY: 'auto'
      }}>
        {loading ? (
          <Loading message="상태 데이터 로딩 중..." size="medium" />
        ) : error ? (
          <p>{error}</p>
        ) : conditionData ? (
          renderConditionData(conditionData)
        ) : (
          <div style={{
            height: '100%',
            overflow: 'auto',
            padding: '15px'
          }}>
            <h3 style={{
              color: '#2c3e50',
              borderBottom: '2px solid #3498db',
              paddingBottom: '10px',
              marginBottom: '20px',
              top: 0,
              backgroundColor: 'white',
            }}>건강 상태</h3>
            
            <p style={{
              textAlign: 'center',
              color: '#6c757d',
              marginTop: '20px'
            }}>
              선택한 날짜의 건강 상태 데이터가 없습니다.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Condition;